import { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Sun, Loader2 } from "lucide-react";

/**
 * SolarReviewCard — user-triggered Google Solar Building Insights review.
 *
 * Contract:
 *  - Never called automatically. The user clicks "Analyze roof with Solar",
 *    reviews the response, then confirms whether to save the derived roof
 *    measurement.
 *  - Backend endpoint `/api/solar/building-insights` is flag-gated. If the
 *    flag is off the endpoint returns 503 with `error_kind=feature_disabled`
 *    and we show a disabled-state message.
 *  - We NEVER expose the Solar API key on the frontend — the backend adapter
 *    is the only holder.
 *
 * We display: imagery date, quality, total roof area (m² and ft²), and the
 * count of roof segments. Raw imagery URLs are not requested and would not
 * be shown if returned (backend already drops them before responding).
 */
export default function SolarReviewCard({ lat, lng, onConfirm, disabled }) {
  const [state, setState] = useState({ status: "idle", insights: null, error: null });

  const analyze = async () => {
    if (lat == null || lng == null) {
      toast.error("Center the map on a property first.");
      return;
    }
    setState({ status: "loading", insights: null, error: null });
    try {
      const { data } = await api.post("/solar/building-insights", {
        latitude: lat,
        longitude: lng,
        required_quality: "HIGH",
      });
      setState({ status: "ready", insights: data.insights, error: null });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const kind = detail?.error_kind || "provider_error";
      const msg =
        kind === "feature_disabled"
          ? "Solar is not enabled in this environment."
          : kind === "provider_not_configured"
          ? "Solar API key not configured."
          : kind === "no_coverage"
          ? "No Solar coverage for this address. Use manual measurement."
          : "Solar API returned an error.";
      setState({ status: "error", insights: null, error: msg });
    }
  };

  return (
    <div
      className="border border-amber-300 bg-amber-50/60 rounded-lg p-3 text-sm"
      data-testid="solar-review-card"
    >
      <div className="flex items-center gap-2 mb-2">
        <Sun className="w-4 h-4 text-amber-600" />
        <div className="font-heading font-bold">Google Solar (roof insights)</div>
      </div>
      <div className="text-xs text-zinc-600 mb-3">
        Query the roof geometry from Google's Solar Building Insights. Nothing is added to your
        measurements until you review and confirm below.
      </div>

      {state.status === "idle" && (
        <Button
          size="sm"
          disabled={disabled}
          onClick={analyze}
          className="bg-amber-600 hover:bg-amber-700 text-white"
          data-testid="solar-analyze-btn"
        >
          Analyze roof with Solar
        </Button>
      )}

      {state.status === "loading" && (
        <div className="flex items-center gap-2 text-xs text-zinc-600" data-testid="solar-loading">
          <Loader2 className="w-3 h-3 animate-spin" /> Contacting Google Solar…
        </div>
      )}

      {state.status === "error" && (
        <div className="text-xs text-rose-700" data-testid="solar-error">
          {state.error}
        </div>
      )}

      {state.status === "ready" && state.insights && (
        <div className="space-y-2" data-testid="solar-insights">
          <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
            <div className="text-zinc-500">Imagery quality</div>
            <div className="font-mono-accent">{state.insights.imagery_quality || "—"}</div>
            <div className="text-zinc-500">Imagery date</div>
            <div className="font-mono-accent">
              {state.insights.imagery_date
                ? `${state.insights.imagery_date.year}-${String(
                    state.insights.imagery_date.month
                  ).padStart(2, "0")}`
                : "—"}
            </div>
            <div className="text-zinc-500">Whole-roof area</div>
            <div className="font-mono-accent">
              {state.insights.total_roof_area_m2
                ? `${Math.round(state.insights.total_roof_area_m2).toLocaleString()} m² (${Math.round(
                    state.insights.total_roof_area_m2 * 10.7639
                  ).toLocaleString()} sq ft)`
                : "—"}
            </div>
            <div className="text-zinc-500">Roof segments</div>
            <div className="font-mono-accent">{state.insights.roof_segments?.length ?? 0}</div>
          </div>
          <div className="flex gap-2">
            <Button
              size="sm"
              onClick={() => onConfirm && onConfirm(state.insights)}
              className="bg-[#FF5A00] hover:bg-[#E04F00] text-white"
              data-testid="solar-confirm-btn"
            >
              Use as roof measurement
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setState({ status: "idle", insights: null, error: null })}
              data-testid="solar-reset-btn"
            >
              Dismiss
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
